import { useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { type CustomerActivation, useCustomerActivation } from '../hooks/useCustomerActivation.ts';
import { useTableUrlState } from '../hooks/useTableUrlState.ts';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.tsx';
import { DataTable } from '../components/data-table/data-table.tsx';
import { DataTableColumnHeader } from '../components/data-table/data-table-column-header.tsx';
import { Button } from '../components/ui/button.tsx';
import { Input } from '../components/ui/input.tsx';
import { Switch } from '../components/ui/switch.tsx';
import { Search } from 'lucide-react';
import { toast } from 'sonner';

export function ActivationStatusPage() {
  const { customers, isLoading, error, updateActiveStatus, isUpdating } = useCustomerActivation();
  const [searchQuery, setSearchQuery] = useState('');
  const tableState = useTableUrlState();

  // Filter customers based on search query
  const filteredCustomers = useMemo(() => {
    if (!searchQuery.trim()) return customers;

    const query = searchQuery.toLowerCase();
    return customers.filter((customer) => {
      return (
        customer.waterscopeAccountName.toLowerCase().includes(query) ||
        customer.waterscopeAccountId?.toLowerCase().includes(query) ||
        customer.salesforceId?.toLowerCase().includes(query) ||
        customer.metronId?.toLowerCase().includes(query) ||
        customer.accountType.toLowerCase().includes(query)
      );
    });
  }, [customers, searchQuery]);

  const handleToggleActive = (customerId: number, currentActive: boolean) => {
    updateActiveStatus(
      { customerId, active: !currentActive },
      {
        onSuccess: () => {
          toast.success(`Customer ${!currentActive ? 'activated' : 'deactivated'} successfully`);
        },
        onError: (error) => {
          toast.error(`Failed to update status: ${error.message}`);
        },
      },
    );
  };

  const columns: ColumnDef<CustomerActivation>[] = [
    {
      accessorKey: 'waterscopeAccountName',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Account Name' />,
      cell: ({ row }) => <div className='font-medium'>{row.getValue('waterscopeAccountName')}</div>,
    },
    {
      accessorKey: 'accountType',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Account Type' />,
    },
    {
      accessorKey: 'waterscopeAccountId',
      header: ({ column }) => <DataTableColumnHeader column={column} title='WaterScope ID' />,
      cell: ({ row }) => {
        const value = row.getValue('waterscopeAccountId') as string | null;
        return value || <span className='text-muted-foreground'>N/A</span>;
      },
    },
    {
      accessorKey: 'metronId',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Metron ID' />,
      cell: ({ row }) => {
        const value = row.getValue('metronId') as string | null;
        return value
          ? <code className='text-xs'>{value}</code>
          : <span className='text-muted-foreground'>N/A</span>;
      },
    },
    {
      accessorKey: 'salesforceId',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Salesforce ID' />,
      cell: ({ row }) => {
        const value = row.getValue('salesforceId') as string | null;
        return value || <span className='text-muted-foreground'>N/A</span>;
      },
    },
    {
      accessorKey: 'active',
      header: ({ column }) => <DataTableColumnHeader column={column} title='Status' />,
      cell: ({ row }) => {
        const customer = row.original;
        return (
          <div className='flex items-center gap-2'>
            <Switch
              checked={customer.active}
              onCheckedChange={() => handleToggleActive(customer.id, customer.active)}
              disabled={isUpdating}
            />
            <span className={customer.active ? 'text-green-600' : 'text-muted-foreground'}>
              {customer.active ? 'Active' : 'Inactive'}
            </span>
          </div>
        );
      },
    },
  ];

  if (isLoading) {
    return (
      <div className='flex items-center justify-center p-8'>
        <p className='text-muted-foreground'>Loading customers...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex items-center justify-center p-8'>
        <p className='text-destructive'>Error: {error}</p>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <Card>
        <CardHeader>
          <CardTitle>Activation Status</CardTitle>
          <CardDescription>
            View and manage customer activation status. Toggle the switch to activate or deactivate
            customers.
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='flex items-center gap-2'>
            <div className='relative flex-1'>
              <Search className='absolute left-2 top-2.5 h-4 w-4 text-muted-foreground' />
              <Input
                placeholder='Search by name, ID, or account type...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='pl-8'
              />
            </div>
            {searchQuery && (
              <Button
                variant='ghost'
                onClick={() => setSearchQuery('')}
                size='sm'
              >
                Clear
              </Button>
            )}
          </div>

          <div className='text-sm text-muted-foreground'>
            Showing {filteredCustomers.length} of {customers.length} customers
          </div>

          <DataTable
            columns={columns}
            data={filteredCustomers}
            sorting={tableState.sorting}
            onSortingChange={tableState.onSortingChange}
            pagination={tableState.pagination}
            onPaginationChange={tableState.onPaginationChange}
          />
        </CardContent>
      </Card>
    </div>
  );
}
